// ============================================================
// caseChangeEvents.ts — (batch 2) "anything the user changes is reflected
// everywhere on their screen at once, no refresh". A save that changes what
// another part of the SAME screen shows announces it here; the parts that
// show it re-read.
//   emitCaseChange(caseId, 'cfa')            — after a successful save
//   useCaseChange(caseId, ['cfa', …], fn)    — fn runs on each matching change
//
// (D12 live updates) OTHER people's changes arrive on the same bus:
// hooks/useLiveTopics turns the server's `case-changed` topics into these
// kinds (topicKinds below) and emits them with `meta.remote = true` + who
// made the change. One emit may carry several kinds; a listener runs ONCE per
// emit, with the first kind it watches and `meta.kinds` = every kind of the
// emit it watches.
// ============================================================
import { useEffect, useRef } from 'react'

export type CaseChangeKind =
  | 'settlementInputs' // matter figures, interims, base costs, recoveries
  | 'cfa'              // the CFA record / funding type
  | 'courtOrder'       // court order / court steps
  | 'litigationFriend' // litigation friend / deputy / attorney / their consent
  | 'clientDetails'    // the client's details (DOB, email, address, consent)
  | 'statement'        // a settlement statement changed
  | 'funding'          // the case's funding (e.g. set to CFA)
  | 'bill'             // a bill / credit note changed
  // (D12) — mostly from other people's changes (hooks/useLiveTopics)
  | 'case'             // header, fields, status, case control, closing, linked cases
  | 'parties'          // defendant / insurer / TPI contacts
  | 'accident'         // accident / injury details
  | 'medical'          // medical agency / experts / records
  | 'witnesses'
  | 'documents'        // files, uploads, deletes, e-signed copies
  | 'deadlines'
  | 'tasks'
  | 'activity'         // notes, activity, time entries, correspondence
  | 'appointments'
  | 'esign'            // any signing event

export const ALL_CASE_CHANGE_KINDS: readonly CaseChangeKind[] = [
  'settlementInputs', 'cfa', 'courtOrder', 'litigationFriend', 'clientDetails', 'statement', 'funding', 'bill',
  'case', 'parties', 'accident', 'medical', 'witnesses', 'documents', 'deadlines', 'tasks', 'activity', 'appointments', 'esign',
]

// (D12) Server topic (case-changed) → the kinds its listeners watch.
const TOPIC_KINDS: Record<string, CaseChangeKind[]> = {
  case: ['case'],
  client: ['clientDetails'],
  litigationFriend: ['litigationFriend'],
  parties: ['parties'],
  accident: ['accident'],
  medical: ['medical'],
  witnesses: ['witnesses'],
  documents: ['documents'],
  deadlines: ['deadlines'],
  tasks: ['tasks'],
  activity: ['activity'],
  appointments: ['appointments'],
  funding: ['funding', 'cfa'],
  billing: ['bill'],
  settlement: ['settlementInputs', 'statement', 'courtOrder'],
  esign: ['esign', 'statement'],
}
export const topicKinds = (topics: readonly string[]): CaseChangeKind[] => {
  const out = new Set<CaseChangeKind>()
  for (const t of topics) (TOPIC_KINDS[t] ?? []).forEach((k) => out.add(k))
  return Array.from(out)
}

export interface CaseChangeMeta {
  // True when someone else (another user / tab / the system) made the change.
  remote?: boolean
  // Who (null / undefined = unknown or the system: a signer, the sealing job).
  actorUserId?: string | null
  // The server topics behind a remote change.
  topics?: string[]
  // A catch-up re-read (reconnect, the tab came back, the stamp moved) — not
  // a specific change.
  resync?: boolean
}
export interface CaseChangeInfo extends CaseChangeMeta {
  // Every kind of this emit that the listener watches.
  kinds: CaseChangeKind[]
}

const EVENT = 'casevo:case-change'
// `kind` = the first of `kinds` (the original single-kind shape, kept for
// listeners that read the event directly).
interface Detail { caseId: string; kind: CaseChangeKind; kinds: CaseChangeKind[]; meta: CaseChangeMeta }

export const emitCaseChange = (caseId: string, kind: CaseChangeKind | readonly CaseChangeKind[], meta: CaseChangeMeta = {}): void => {
  if (typeof window === 'undefined') return
  const kinds = typeof kind === 'string' ? [kind] : [...kind]
  if (!kinds.length) return
  window.dispatchEvent(new CustomEvent<Detail>(EVENT, { detail: { caseId, kind: kinds[0], kinds, meta } }))
}

export const useCaseChange = (
  caseId: string | null | undefined,
  kinds: readonly CaseChangeKind[] | '*',
  fn: (kind: CaseChangeKind, info: CaseChangeInfo) => void,
): void => {
  const fnRef = useRef(fn)
  fnRef.current = fn
  const key = kinds === '*' ? '*' : kinds.join(',')
  useEffect(() => {
    if (!caseId) return undefined
    const want = key === '*' ? null : new Set(key.split(','))
    const on = (e: Event): void => {
      const d = (e as CustomEvent<Detail>).detail
      if (!d || d.caseId !== caseId) return
      const matched = want ? d.kinds.filter((k) => want.has(k)) : d.kinds
      if (!matched.length) return
      fnRef.current(matched[0], { ...d.meta, kinds: matched })
    }
    window.addEventListener(EVENT, on)
    return () => window.removeEventListener(EVENT, on)
  }, [caseId, key])
}
